import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {AppThunk, RootState} from "../../../store";
import {Color} from "../../color/color";
import {LocationCoordinate} from "../../location/slice";
import {coordinateToBounds, roundCoordinate} from "../../map/tools";
import {MapBounds} from "../../map/slice";
import { track } from "../../../../helpers/analytics";

// Lazy import to break the circular dependency:
//   slice.ts → TilesProvider.ts → slice.ts
// TilesProvider is only needed at runtime (inside thunks), never at module evaluation time.
const getTilesProvider = (): any => require('./TilesProvider').default;
const getSessionTileCountFn = (): (() => number) => require('./TilesProvider').getSessionTileCount;

export interface TilesState {
    tiles: { [lng: number]: { [lat: number]: Tile } };
    isLoading: boolean;
}

export interface Tile {
    bounds: MapBounds,
    coordinate: LocationCoordinate,
    color: Color,
    userId?: string,
    /** Persistent cosmetic flags set at paint time while the effect was enabled */
    glow?: boolean,
    sparkle?: boolean,
}

export const selectTiles = (state: RootState) => state.tiles.tiles;
export const selectTilesLoading = (state: RootState) => state.tiles.isLoading;

const initialState: TilesState = {
    tiles: {},
    isLoading: false,
};

export const sliceName = 'tiles';

export interface PaintCoordinates {
    coordinates: LocationCoordinate[];
}

export interface LocationCoordinateAndRadius {
    coordinate: LocationCoordinate,
    radiusInMeters: number,
}

export const tilesSlice = createSlice({
    name: sliceName,
    initialState: initialState,
    reducers: {
        _applyPaintedTiles(state, action: PayloadAction<Tile[]>) {
            const currentTiles: { [lng: number]: { [lat: number]: Tile } } = {...state.tiles};
            for (const tile of action.payload) {
                if (!(tile.coordinate.longitude in currentTiles)) {
                    currentTiles[tile.coordinate.longitude] = {};
                }
                currentTiles[tile.coordinate.longitude][tile.coordinate.latitude] = tile;
            }
            state.tiles = currentTiles;
        },
        setTiles(state, action: PayloadAction<Tile[]>) {
            const nextTiles: { [lng: number]: { [lat: number]: Tile } } = {...state.tiles};
            for (const tile of action.payload) {
                if (!(tile.coordinate.longitude in nextTiles)) {
                    nextTiles[tile.coordinate.longitude] = {};
                }
                nextTiles[tile.coordinate.longitude][tile.coordinate.latitude] = tile;
            }
            state.tiles = nextTiles;
        },
        clearTiles(state) {
            state.tiles = {};
        },
        // Drop tiles whose coordinate falls outside `bounds`. Called on every
        // viewport refetch so the tile dict stays bounded to what's near the
        // screen — without this, panning across a city accumulates every tile
        // ever loaded and the growing GeoJSON eventually OOMs the tab.
        retainTilesWithin(state, action: PayloadAction<MapBounds>) {
            const { north, south, east, west } = action.payload;
            const next: { [lng: number]: { [lat: number]: Tile } } = {};
            for (const lngKey in state.tiles) {
                const lng = Number(lngKey);
                if (lng < west || lng > east) continue;
                const column = state.tiles[lngKey];
                let kept: { [lat: number]: Tile } | undefined;
                for (const latKey in column) {
                    const lat = Number(latKey);
                    if (lat < south || lat > north) continue;
                    if (!kept) kept = {};
                    kept[lat] = column[latKey];
                }
                if (kept) next[lng] = kept;
            }
            state.tiles = next;
        },
        setLoading(state, action: PayloadAction<boolean>) {
            state.isLoading = action.payload;
        },
    }
});

export const {_applyPaintedTiles, setTiles, clearTiles, retainTilesWithin, setLoading} = tilesSlice.actions;

// Thunks (side effects — not reducers)

export const getTiles = (payload: LocationCoordinateAndRadius): AppThunk => () => {
    getTilesProvider().instance.getTiles(payload.coordinate, payload.radiusInMeters);
};

export const disconnect = (): AppThunk => () => {
    getTilesProvider().instance.disconnect();
};

export default tilesSlice.reducer;
